import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import { buildClinicPortalUrl, formatClinicHost } from "../../../utils/portalHost";
import { trackPaymentSuccess } from "../../../analytics/metrikaGoals";
import { clinicStatusLabel } from "./platformClubsUtils";
import { formatRub, MetricCard } from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

export default function PlatformTenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clinic, setClinic] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState({
    saas_plan_id: "",
    status: "",
    crm_monthly_price_rub: "",
    crm_billing_period: "monthly",
    crm_paid_until: "",
  });
  const [checkout, setCheckout] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const load = () => {
    setUsersLoading(true);
    Promise.all([
      platformAPI.getClinic(id),
      platformAPI.getPlans(),
      platformAPI.listClinicUsers(id),
      platformAPI.getFeaturesCatalog().catch(() => ({ features: [] })),
    ])
      .then(([detail, plansRes, usersRes, featRes]) => {
        const c = detail.clinic;
        setClinic(c);
        setEntitlements(detail.entitlements);
        setFeatureCatalog(featRes.features || []);
        setPlans(plansRes.plans?.filter((p) => p.is_active) || []);
        setUsers(usersRes.users || []);
        setBilling({
          saas_plan_id: c.saas_plan_id || "",
          status:
            c.billing?.is_paid && !c.billing?.is_trial
              ? "active"
              : c.billing?.is_demo_expired
                ? "trial"
                : c.billing?.status || c.status,
          crm_monthly_price_rub: c.crm_monthly_price_rub ?? "",
          crm_billing_period: c.crm_billing_period || "monthly",
          crm_paid_until: c.billing?.paid_until
            ? String(c.billing.paid_until).slice(0, 10)
            : c.crm_paid_until
              ? String(c.crm_paid_until).slice(0, 10)
              : "",
        });
      })
      .catch((e) => setErr(e.response?.data?.error || "Клуб не найден"))
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      trackPaymentSuccess({ source: "platform_admin" });
      setMsg("Оплата принята. Синхронизируем подписку…");
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
      load();
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!id || !billing.saas_plan_id) {
      setCheckout(null);
      return;
    }
    let cancelled = false;
    setCheckoutLoading(true);
    platformAPI
      .getClinicSubscriptionCheckout(id, {
        saas_plan_id: billing.saas_plan_id,
        crm_billing_period: billing.crm_billing_period,
      })
      .then((res) => {
        if (!cancelled) setCheckout(res.checkout || null);
      })
      .catch(() => {
        if (!cancelled) setCheckout(null);
      })
      .finally(() => {
        if (!cancelled) setCheckoutLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, billing.saas_plan_id, billing.crm_billing_period]);

  const featureNameByKey = useMemo(() => {
    const map = {};
    for (const f of featureCatalog) map[f.key] = f.name;
    return map;
  }, [featureCatalog]);

  const saveBilling = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    try {
      await platformAPI.updateClinicBilling(id, {
        saas_plan_id: billing.saas_plan_id || null,
        status: billing.status,
        crm_monthly_price_rub:
          billing.crm_monthly_price_rub === ""
            ? null
            : Number(billing.crm_monthly_price_rub),
        crm_billing_period: billing.crm_billing_period,
        crm_paid_until: billing.crm_paid_until || null,
      });
      setMsg("Биллинг обновлён");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Ошибка сохранения");
    }
  };

  const payOnline = async () => {
    if (!billing.saas_plan_id) {
      setErr("Выберите SaaS-тариф для оплаты");
      return;
    }
    setErr("");
    setMsg("");
    setPayBusy(true);
    try {
      const data = await platformAPI.initClinicSubscriptionPayment(id, {
        saas_plan_id: billing.saas_plan_id,
        crm_billing_period: billing.crm_billing_period,
      });
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        setErr("Ссылка на оплату не получена");
      }
    } catch (e2) {
      setErr(e2.response?.data?.error || "Онлайн-оплата недоступна");
    } finally {
      setPayBusy(false);
    }
  };

  const deleteClinic = async () => {
    if (deleteBusy) return;
    if (
      !window.confirm(
        "Удалить клуб? Клуб исчезнет из списка, доступ пользователей будет заблокирован, slug освободится. Данные останутся для статистики.",
      )
    ) {
      return;
    }
    setErr("");
    setMsg("");
    setDeleteBusy(true);
    try {
      await platformAPI.deleteClinic(id);
      navigate("/admin/tenants", {
        replace: true,
        state: { msg: "Клуб удалён из списка" },
      });
    } catch (e2) {
      setErr(e2.response?.data?.error || "Не удалось отключить клуб");
      setDeleteBusy(false);
    }
  };

  if (!clinic && !err) {
    return <div className={s.loading}>Загрузка клуба…</div>;
  }

  if (!clinic) {
    return (
      <div className={s.page}>
        <div className={s.alertError}>{err}</div>
        <Link to="/admin/tenants" className={s.heroActionBtnGhost}>
          ← К списку клубов
        </Link>
      </div>
    );
  }

  const features = entitlements?.features || {};
  const selectedPlan = plans.find((p) => p.id === billing.saas_plan_id);

  return (
    <div className={s.page}>
      <PlatformPageHeader
        backTo="/admin/tenants"
        backLabel="Клубы"
        eyebrow="Карточка клуба"
        title={clinic.name}
        lead={
          <>
            <a
              href={buildClinicPortalUrl(clinic.subdomain, "/manage")}
              target="_blank"
              rel="noopener noreferrer"
              className={s.inlineLink}
            >
              {formatClinicHost(clinic.subdomain)}
            </a>
            {" · "}
            {clinicStatusLabel(clinic)}
            {clinic.billing?.plan_label ? ` · ${clinic.billing.plan_label}` : ""}
            {clinic.billing?.paid_until
              ? ` · до ${String(clinic.billing.paid_until).slice(0, 10)}`
              : ""}
          </>
        }
        actions={
          <>
            <a
              href={buildClinicPortalUrl(clinic.subdomain, "/manage")}
              target="_blank"
              rel="noopener noreferrer"
              className={s.heroActionBtn}
            >
              Портал
            </a>
            <button
              type="button"
              className={s.heroActionBtnDanger}
              onClick={deleteClinic}
              disabled={deleteBusy}
            >
              {deleteBusy ? "Отключение…" : "Отключить"}
            </button>
          </>
        }
        compactOnMobile={false}
      />

      {msg ? <div className={s.alertSuccess}>{msg}</div> : null}
      {err ? <div className={s.alertError}>{err}</div> : null}

      <section className={s.metricGrid}>
        <MetricCard label="Тренеры" value={clinic.stats?.trainers ?? 0} />
        <MetricCard label="Клиенты" value={clinic.stats?.clients ?? 0} accent />
        <MetricCard
          label="Абонементы"
          value={clinic.stats?.activeMemberships ?? 0}
        />
        <MetricCard
          label="Выручка 30 дн."
          value={`${formatRub(clinic.stats?.revenue_30d)} ₽`}
        />
      </section>

      {clinic.owner || clinic.activity ? (
        <section className={s.chartPanel}>
          <h3 className={s.panelTitle}>Владелец и активность</h3>
          <div className={s.ownerDetailGrid}>
            <div className={s.infoBlock}>
              <div className={s.metricLabel}>Владелец</div>
              <strong className={s.infoStrong}>
                {clinic.owner?.full_name || "—"}
              </strong>
              <div className={s.clubOwnerLinks}>
                {clinic.owner?.email ? (
                  <a
                    href={`mailto:${clinic.owner.email}`}
                    className={s.clubContactLink}
                  >
                    {clinic.owner.email}
                  </a>
                ) : null}
                {clinic.owner?.phone ? (
                  <a
                    href={`tel:${clinic.owner.phone}`}
                    className={s.clubContactLink}
                  >
                    {clinic.owner.phone}
                  </a>
                ) : null}
              </div>
            </div>
            <div className={s.infoBlock}>
              <div className={s.metricLabel}>Последняя активность</div>
              <strong className={s.infoStrong}>
                {clinic.activity?.last_activity_at
                  ? new Date(clinic.activity.last_activity_at).toLocaleString(
                      "ru-RU",
                    )
                  : "Нет данных"}
              </strong>
              <p className={s.infoMuted}>
                Вход:{" "}
                {clinic.activity?.last_staff_login_at
                  ? new Date(
                      clinic.activity.last_staff_login_at,
                    ).toLocaleString("ru-RU")
                  : "—"}
                <br />
                Занятие:{" "}
                {clinic.activity?.last_session_at
                  ? new Date(clinic.activity.last_session_at).toLocaleString(
                      "ru-RU",
                    )
                  : "—"}
              </p>
            </div>
          </div>
        </section>
      ) : null}

      <form className={s.chartPanel} onSubmit={saveBilling}>
        <div className={s.panelHeaderRow}>
          <h3 className={s.panelTitle}>Тариф и биллинг</h3>
          {selectedPlan ? (
            <span className={s.planBadgeSoft}>{selectedPlan.name}</span>
          ) : null}
        </div>

        <div className={s.formGrid}>
          <label className={s.field}>
            <span>SaaS-тариф</span>
            <select
              value={billing.saas_plan_id}
              onChange={(e) =>
                setBilling({ ...billing, saas_plan_id: e.target.value })
              }
            >
              <option value="">По legacy tier</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.monthly_price_rub} ₽/мес)
                </option>
              ))}
            </select>
          </label>
          <label className={s.field}>
            <span>Статус</span>
            <select
              value={billing.status}
              onChange={(e) =>
                setBilling({ ...billing, status: e.target.value })
              }
            >
              <option value="trial">Пробный</option>
              <option value="active">Активен</option>
              <option value="suspended">Отключён</option>
            </select>
          </label>
          <label className={s.field}>
            <span>Цена CRM / мес (₽)</span>
            <input
              type="number"
              min="0"
              value={billing.crm_monthly_price_rub}
              onChange={(e) =>
                setBilling({
                  ...billing,
                  crm_monthly_price_rub: e.target.value,
                })
              }
            />
          </label>
          <label className={s.field}>
            <span>Период оплаты</span>
            <select
              value={billing.crm_billing_period}
              onChange={(e) =>
                setBilling({
                  ...billing,
                  crm_billing_period: e.target.value,
                })
              }
            >
              <option value="monthly">Месяц</option>
              <option value="yearly">Год</option>
            </select>
          </label>
          <label className={s.field}>
            <span>
              {billing.status === "trial" ? "Демо до" : "Демо / оплачено до"}
            </span>
            <input
              type="date"
              value={billing.crm_paid_until}
              onChange={(e) =>
                setBilling({ ...billing, crm_paid_until: e.target.value })
              }
            />
          </label>
        </div>

        <div className={s.checkoutStrip}>
          <div>
            <div className={s.metricLabel}>К оплате</div>
            <strong className={s.checkoutAmount}>
              {checkoutLoading
                ? "…"
                : checkout?.amount_rub != null
                  ? `${formatRub(checkout.amount_rub)} ₽`
                  : "—"}
            </strong>
            {checkout?.checkout_type === "change" && checkout.credit_rub > 0 ? (
              <p className={s.infoMuted}>
                Зачёт неиспользованных дней: −{formatRub(checkout.credit_rub)} ₽
              </p>
            ) : (
              <p className={s.infoMuted}>
                {billing.crm_billing_period === "yearly"
                  ? "Годовой период"
                  : "Месячный период"}
              </p>
            )}
          </div>
          <div className={s.formActions}>
            <button type="submit" className={s.heroActionBtnGhost}>
              Сохранить
            </button>
            <button
              type="button"
              className={s.heroActionBtn}
              disabled={payBusy || checkoutLoading || !billing.saas_plan_id}
              onClick={payOnline}
            >
              {payBusy
                ? "Подготовка…"
                : checkout?.amount_rub
                  ? `Оплатить ${formatRub(checkout.amount_rub)} ₽`
                  : "Оплатить онлайн"}
            </button>
          </div>
        </div>
      </form>

      <section className={s.chartPanel}>
        <div className={s.panelHeaderRow}>
          <h3 className={s.panelTitle}>Пользователи</h3>
          <span className={s.panelCount}>{users.length}</span>
        </div>
        {usersLoading ? (
          <p className={s.emptyHint}>Загрузка…</p>
        ) : users.length ? (
          <div className={s.userCards}>
            {users.map((u) => (
              <div key={u.id} className={s.userCard}>
                <div className={s.userCardMain}>
                  <strong>{u.full_name || u.email}</strong>
                  <small>
                    {u.email}
                    {u.phone ? ` · ${u.phone}` : ""}
                  </small>
                  <div className={s.userCardMeta}>
                    <span className={s.statusPill}>{u.role}</span>
                    <span
                      className={`${s.statusPill} ${
                        u.is_blocked ? s.statusSuspended : s.statusActive
                      }`}
                    >
                      {u.is_blocked ? "Заблокирован" : "Активен"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={s.emptyHint}>Пользователей нет</p>
        )}
      </section>

      <section className={s.chartPanel}>
        <h3 className={s.panelTitle}>
          Функции тарифа
          {entitlements?.plan?.name ? ` — ${entitlements.plan.name}` : ""}
        </h3>
        <div className={s.featureChips}>
          {Object.entries(features).map(([key, val]) => {
            const on = val === true || (typeof val === "number" && val > 0);
            return (
              <span
                key={key}
                className={`${s.featureChip} ${on ? s.featureChipOn : s.featureChipOff}`}
              >
                {featureNameByKey[key] || key}
                {typeof val === "number" ? `: ${val}` : ""}
              </span>
            );
          })}
        </div>
      </section>
    </div>
  );
}
